import { format } from 'date-fns';
import { mixed, object } from 'yup';
import { tableSortFilter } from '@/utils/arrayProcessing';
import {
  BlockedDateServiceVariant,
  ServiceBlockedDate,
  ServiceBlockedDateFormValues,
  ServiceBlockedDatePayload,
} from '../types';

export const BLOCKED_DATES_QUERY_KEY = 'serviceBlockedDates';

export const BLOCKED_DATES_TABLE_HEAD = [
  { id: 'start_date', label: 'Start date', align: 'left' },
  { id: 'end_date', label: 'End date', align: 'left' },
  { id: 'day_of_the_week', label: 'Day', align: 'left' },
  { id: 'city.name', label: 'City', align: 'left' },
  { id: 'service_variant.name', label: 'Service variant', align: 'left' },
  { id: 'service_variant.service.name', label: 'Service', align: 'left' },
  { id: 'actions', label: '', align: 'right' },
];

export const BLOCKED_DATE_DEFAULT_VALUES: ServiceBlockedDateFormValues = {
  service_variant_id: '',
  city_id: '',
  start_date: '',
  end_date: '',
  day_of_the_week: '',
};

export const DAY_OF_WEEK_OPTIONS = [
  { value: '', label: 'All days' },
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

const DAY_LABELS: Record<number, string> = {
  0: 'Sunday',
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
  7: 'Sunday',
};

const dateOnlyPattern = /^\d{4}-\d{2}-\d{2}$/;

export const parseDateForComparison = (value: string | Date | null) => {
  if (!value) return null;

  if (value instanceof Date) {
    const date = new Date(value);
    date.setHours(0, 0, 0, 0);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (dateOnlyPattern.test(value)) {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatDateForApi = (value: string | Date | null) => {
  if (!value) return '';
  if (typeof value === 'string' && dateOnlyPattern.test(value)) return value;

  const date = value instanceof Date ? value : new Date(value);
  return format(date, 'yyyy-MM-dd');
};

export const formatBlockedDateDisplay = (
  value: string | Date | null,
  outputFormat = 'EEE, dd MMM yyyy'
) => {
  const date = parseDateForComparison(value);
  return date ? format(date, outputFormat) : 'N/A';
};

export const BLOCKED_DATE_SCHEMA = object().shape({
  start_date: mixed()
    .required('Start date is required')
    .test('valid-start-date', 'Start date is required', (value) =>
      Boolean(parseDateForComparison(value as string | Date | null))
    ),
  end_date: mixed()
    .required('End date is required')
    .test('valid-end-date', 'End date is required', (value) =>
      Boolean(parseDateForComparison(value as string | Date | null))
    )
    .test(
      'is-on-or-after-start',
      'End date must be on or after start date',
      function (endDate) {
        const { start_date } = this.parent as ServiceBlockedDateFormValues;
        const start = parseDateForComparison(start_date);
        const end = parseDateForComparison(endDate as string | Date | null);

        return start && end ? end >= start : true;
      }
    ),
});

export const getDayOfWeekLabel = (day: number | string | null | undefined) => {
  if (day === null || day === undefined || day === '') return 'All days';

  const normalizedDay = Number(day);
  return DAY_LABELS[normalizedDay] ?? 'N/A';
};

export const getBlockedDateCityName = (blockedDate: ServiceBlockedDate) => {
  if (blockedDate.city_id === null) return 'All cities';
  return blockedDate.city?.name ?? blockedDate.cities?.name ?? 'N/A';
};

export const getBlockedDateVariantName = (blockedDate: ServiceBlockedDate) => {
  if (blockedDate.service_variant_id === null) return 'All variants';
  return getBlockedDateServiceVariantName(blockedDate.service_variant);
};

export const getBlockedDateServiceName = (blockedDate: ServiceBlockedDate) =>
  blockedDate.service_variant?.service?.name ??
  blockedDate.service_variant?.service_name ??
  'N/A';

export const getBlockedDateServiceVariantName = (
  variant:
    | (Partial<BlockedDateServiceVariant> & { name?: string })
    | null
    | undefined
) => variant?.variant_name ?? variant?.name ?? 'N/A';

export const buildServiceBlockedDatePayload = (
  values: ServiceBlockedDateFormValues
): ServiceBlockedDatePayload => ({
  city_id: values.city_id === '' ? null : Number(values.city_id),
  service_variant_id:
    values.service_variant_id === '' ? null : Number(values.service_variant_id),
  start_date: formatDateForApi(values.start_date),
  end_date: formatDateForApi(values.end_date),
  day_of_the_week:
    values.day_of_the_week === '' ? null : Number(values.day_of_the_week),
});

export function applySortFilterBlockedDates({
  tableData,
  comparator,
}: {
  tableData: ServiceBlockedDate[];
  comparator: (a: any, b: any) => number;
}) {
  return tableSortFilter(tableData, comparator);
}
